import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Character } from './types/character';
import { CharacterCard } from './components/CharacterCard';
import { Sparkles, Upload, Search, User, Swords } from 'lucide-react';
import { onAuthStateChange, AuthUser } from './lib/auth';
import { updateLocalCharacters, deleteUserCharacter, saveCharacter } from './lib/characters';
import { getSharedCharacter } from './utils/sharing';
import ImportModal from './components/ImportModal';
import SearchModal from './components/SearchModal';
import CharacterPreview from './components/CharacterPreview';
import AuthModal from './components/AuthModal';
import UserMenu from './components/UserMenu';
import PaywallModal from './components/PaywallModal';
import { NewCharacterButton } from './components/NewCharacterButton';
import { getThemeSettings, getCurrentSeasonalTheme, getThemeColors } from './lib/themes';
import { apiGet, apiPost } from './lib/api';

const STORAGE_KEY = 'character-creator-data';
const FREE_LIMIT = 2;

function App() {
  const [characters, setCharacters] = useState<Character[]>(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        return Array.isArray(parsedData) ? parsedData.map(char => ({
          ...char,
          isStarred: !!char.isStarred,
          lastModified: char.lastModified || Date.now(),
          tags: Array.isArray(char.tags) ? char.tags : [],
          containerBackground: char.containerBackground || { type: 'color', value: 'bg-white' }
        })) : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [sharedCharacter, setSharedCharacter] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  const themeSettings = getThemeSettings();
  const seasonalTheme = getCurrentSeasonalTheme();
  const themeColors = getThemeColors();

  // Sauvegarde localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(characters));
    } catch {}
  }, [characters]);

  // Auth state
  useEffect(() => {
    const { data: { subscription } } = onAuthStateChange((u) => {
      setUser(u as AuthUser | null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Sync au login
  useEffect(() => {
    if (user) {
      updateLocalCharacters(characters)
        .then(merged => { if (merged) setCharacters(merged); })
        .catch(console.error);
    }
  }, [user?.id]);

  // Vérifier paiement Stripe au retour
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');

    if (payment === 'success' && sessionId && user) {
      apiGet(`/api/stripe/verify-session?session_id=${sessionId}`)
        .then(() => {
          window.history.replaceState({}, '', '/');
          // Rafraîchir le statut user
          apiGet<{ user: AuthUser }>('/api/auth/me').then(({ user: u }) => setUser(u));
        })
        .catch(console.error);
    } else if (payment) {
      window.history.replaceState({}, '', '/');
    }
  }, [user]);

  // Lien de partage
  useEffect(() => {
    const checkShared = async () => {
      const shareId = window.location.pathname.split('/share/')[1]?.split('/')[0] ||
        new URLSearchParams(window.location.search).get('share');
      if (shareId) {
        try {
          const character = await getSharedCharacter(shareId);
          if (character) setSharedCharacter(character);
          else window.history.replaceState({}, '', '/');
        } catch {
          window.history.replaceState({}, '', '/');
        }
      }
    };
    checkShared();
  }, []);

  const handleImport = (importedCharacters: Character[]) => {
    const validated = importedCharacters.map(char => ({
      ...char,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isStarred: false,
      lastModified: Date.now(),
      tags: Array.isArray(char.tags) ? char.tags : [],
      containerBackground: char.containerBackground || { type: 'color', value: 'bg-white' }
    }));
    setCharacters(prev => [...prev, ...validated]);
    setShowImportModal(false);
    setImportError(null);
  };

  const handleNewCharacter = useCallback(async (character: Character) => {
    if (!user) {
      // Non connecté : limite locale
      if (characters.length >= FREE_LIMIT) {
        setShowPaywall(true);
        return;
      }
      setCharacters(prev => [...prev, character]);
      return;
    }

    // Connecté : vérifier limite serveur
    try {
      setIsLoading(true);
      await saveCharacter(character);
      setCharacters(prev => [...prev, character]);
    } catch (err: unknown) {
      if ((err as { error?: string }).error === 'LIMIT_REACHED') {
        setShowPaywall(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, characters.length]);

  const updateCharacter = useCallback(async (updatedCharacter: Character) => {
    const newChar = { ...updatedCharacter, lastModified: Date.now() };
    setCharacters(prev => prev.map(c => c.id === newChar.id ? newChar : c));
    if (user) {
      try { await saveCharacter(newChar); } catch (e) { console.error('Erreur sauvegarde:', e); }
    }
  }, [user]);

  const deleteCharacter = useCallback(async (id: string) => {
    setCharacters(prev => prev.filter(c => c.id !== id));
    if (user) {
      try { await deleteUserCharacter(id); } catch (e) { console.error('Erreur suppression:', e); }
    }
  }, [user]);

  if (sharedCharacter) {
    return (
      <div className="min-h-screen">
        <div className={`fixed inset-0 ${themeColors.background}`}>
          <AnimatePresence>
            <motion.div className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob1" />
              <motion.div className="absolute top-1/3 -right-48 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob2" />
              <motion.div className="absolute -bottom-48 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob3" />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="relative">
          <div className="container mx-auto px-4">
            <CharacterPreview
              character={sharedCharacter}
              onClose={() => { setSharedCharacter(null); window.history.pushState({}, '', '/'); }}
              readOnly
              background="bg-transparent"
            />
          </div>
        </div>
      </div>
    );
  }

  const sortedCharacters = [...characters].sort((a, b) => {
    if (a.isStarred && !b.isStarred) return -1;
    if (!a.isStarred && b.isStarred) return 1;
    return (b.lastModified || 0) - (a.lastModified || 0);
  });

  const isPremium = user?.is_premium === 1;
  const atLimit = !isPremium && characters.length >= FREE_LIMIT;

  // Animation variants pour les cartes
  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.08,
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1] as const,
      },
    }),
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 },
    },
  };

  // Animation pour le header
  const headerVariants: Variants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background mesh gradient anime */}
      <div className={`fixed inset-0 ${themeColors.background} overflow-hidden`}>
        {/* Gradient overlay anime */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              'radial-gradient(ellipse at 20% 50%, rgba(120, 50, 200, 0.3) 0%, transparent 50%)',
              'radial-gradient(ellipse at 80% 20%, rgba(80, 50, 200, 0.3) 0%, transparent 50%)',
              'radial-gradient(ellipse at 20% 50%, rgba(120, 50, 200, 0.3) 0%, transparent 50%)',
            ],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        {/* Blobs animes */}
        <motion.div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/60 rounded-full mix-blend-multiply filter blur-2xl animate-blob1"
          animate={{ scale: [1, 1.1, 0.95, 1] }} transition={{ duration: 20, repeat: Infinity }} />
        <motion.div className="absolute top-1/3 -right-48 w-96 h-96 bg-indigo-500/60 rounded-full mix-blend-multiply filter blur-2xl animate-blob2"
          animate={{ scale: [1, 0.95, 1.1, 1] }} transition={{ duration: 25, repeat: Infinity }} />
        <motion.div className="absolute -bottom-48 left-1/2 transform -translate-x-1/2 w-[500px] h-[500px] bg-pink-500/50 rounded-full mix-blend-multiply filter blur-2xl animate-blob3"
          animate={{ scale: [1, 1.05, 0.98, 1] }} transition={{ duration: 30, repeat: Infinity }} />
        {/* Particules subtiles */}
        <motion.div className="absolute top-1/3 left-1/4 w-1 h-1 bg-white/30 rounded-full animate-float" />
        <motion.div className="absolute top-2/3 left-3/4 w-1.5 h-1.5 bg-white/20 rounded-full animate-float" style={{ animationDelay: '1s' }} />
        <motion.div className="absolute top-1/2 left-1/6 w-1 h-1 bg-purple-200/40 rounded-full animate-float" style={{ animationDelay: '2s' }} />
        <motion.div className="absolute top-3/4 left-2/3 w-0.5 h-0.5 bg-white/25 rounded-full animate-float" style={{ animationDelay: '0.5s' }} />
      </div>

      <div className="relative">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header moderne glass-morphism */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={headerVariants}
            className="mb-10"
          >
            <div className="glass-card-strong rounded-2xl p-6 mb-8">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <motion.div
                      className="p-3 bg-white/15 rounded-xl"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <Sparkles className="w-6 h-6 text-white" />
                    </motion.div>
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-purple-300 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">Createur de Personnages</h1>
                    <p className="text-purple-200/80 text-sm mt-0.5">
                      {isPremium
                        ? 'Premium - Fiches illimitees'
                        : `${characters.length} / ${FREE_LIMIT} fiches gratuites`
                      }
                    </p>
                    {/* Barre de progression */}
                    {!isPremium && (
                      <div className="mt-2 h-1.5 w-32 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min((characters.length / FREE_LIMIT) * 100, 100)}%` }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <motion.button
                    onClick={() => setShowSearchModal(true)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-glass px-4 py-2.5 text-white rounded-xl flex items-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    <span className="text-sm font-medium">Rechercher</span>
                  </motion.button>
                  <motion.button
                    onClick={() => setShowImportModal(true)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-glass px-4 py-2.5 text-white rounded-xl flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="text-sm font-medium">Importer</span>
                  </motion.button>
                  <div>
                    <NewCharacterButton
                      onCharacterCreate={handleNewCharacter}
                      isLoading={isLoading}
                      disabled={atLimit}
                      onLimitReached={() => setShowPaywall(true)}
                    />
                  </div>
                  {user ? (
                    <UserMenu user={{ email: user.email }} />
                  ) : (
                    <motion.button
                      onClick={() => setShowAuthModal(true)}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl flex items-center gap-2 text-sm font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
                    >
                      <User className="w-4 h-4" />
                      Se connecter
                    </motion.button>
                  )}
                  <motion.a
                    href="/persona-multi/"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl flex items-center gap-2 text-sm font-medium shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-shadow"
                  >
                    <Swords className="w-4 h-4" />
                    Multijoueur
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Grille des personnages */}
          {sortedCharacters.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <motion.div
                className="text-6xl mb-4 animate-float"
                style={{ animationDelay: '0.5s' }}
              >
                ✨
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Aucun personnage encore</h2>
              <p className="text-purple-200/70 mb-6">Creez votre premier personnage pour commencer !</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedCharacters.map((character, i) => (
                <motion.div
                  key={character.id}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={cardVariants}
                >
                  <CharacterCard character={character} onUpdate={updateCharacter} onDelete={deleteCharacter} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showImportModal && (
        <ImportModal onImport={handleImport} onClose={() => { setShowImportModal(false); setImportError(null); }} error={importError} />
      )}
      {showSearchModal && <SearchModal onClose={() => setShowSearchModal(false)} />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={() => setShowAuthModal(false)} />}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </div>
  );
}

export default App;